import { get, post, put, fetchDelete } from './fetchUtils.js';

const myCache = new Map();

const HPR_BASE_URL = `${import.meta.env.VITE_BACKEND_URL}/hrp-proxy`; //|| process.env.HRP_PROXY_URL
const CLIENT_DOCUMENT_URL =
  import.meta.env.VITE_CLIENT_DOCUMENT_URL ?? import.meta.env.VITE_BACKEND_URL;

const headers = new Headers({
  'content-type': 'application/json',
  accept: 'application/json',
  'x-api-key': 'shhh...',
});

const queryParamsToPathParams = ({ params, path }) => {
  if (params != null) {
    for (const key of Object.keys(params)) {
      if (params[key]) {
        path += `/${key}/${params[key]}`;
      }
    }
  }

  return path;
};

//auth
const sendToken = async (body) => {
  const result = await post({
    endpoint: '/auth/send-token',
    body,
  });
  if (result.errorType) {
    throw new Error(result.errorMessage);
  }

  return result;
};

const verifyToken = async (body) => {
  const result = await post({
    endpoint: '/auth/verify-token',
    body,
  });

  //cache data on load
  get({
    endpoint: '/hrp-proxy/benefitPlan/db/getBenefitTier',
    queryParams: {},
  })
    .then((allBeneftiTeirs) => {
      allBeneftiTeirs.rows.forEach((row) => {
        myCache.set(row.benefitTierKey, row);
      });
    })
    .catch((err) => console.log(err));

  return result;
};

//template
const fetchTemplates = async () => {
  const results = await get({ endpoint: '/template/getTemplates' });
  //  console.log('fetchTemplates', results)
  const options = results.aggregations.unique_templates.buckets.map(
    (bucket) => ({
      value: bucket.key[1], //benefitPlanTemplateKey
      label: `${bucket.key[1]}: ${bucket.key[0]} (${bucket.doc_count})`,
    }),
  );

  return options;
};

/**
 * Fetches the notification template catalog (template id + automated/manual send mode) for the Notifications page sidebar.
 * Calls `GET /notifications/template-list`.
 * @returns {Promise<{ templateList: { template: string, sendType: string }[] }>}
 */
const fetchNotificationTemplateList = async () => {
  return await get({
    endpoint: '/notifications/template-list',
  });
};

/**
 * Updates a template's send type (stub backend until persistence exists).
 * @param {{ template: string, sendType: 'manual' | 'automated' }} body
 * @returns {Promise<unknown>} Parsed JSON response from POST `/notifications/update-template-send-type`.
 */
const updateNotificationTemplateSendType = async (body) => {
  return await post({
    endpoint: '/notifications/update-template-send-type',
    body,
  });
};

/**
 * Shared query params for notification queue list, CSV export, and total count (no `cursor`).
 *
 * @param {{ sendType: string, queueFilter: string | null, columnFilters?: Record<string, string> }} params
 * @returns {Record<string, string>}
 */
function buildNotificationsFilterQueryParams({
  sendType,
  queueFilter,
  columnFilters = {},
}) {
  const queryParams = {
    sendType,
    status: queueFilter == null ? 'all' : queueFilter,
  };

  for (const [key, value] of Object.entries(columnFilters)) {
    const trimmed = String(value).trim();
    if (trimmed !== '') {
      queryParams[key] = key === 'type' ? trimmed.toUpperCase() : trimmed;
    }
  }

  return queryParams;
}

/**
 * Fetches one page of notifications for the queue infinite list.
 * Calls `GET /notifications` with `sendType`, `status` (`pending` = PENDING only; `errors` = ERROR, CORRESPONDENCE_HRP_UPDATE_ERROR per v3 enum; `CANCELED` and `CORRESPONDENCE_HRP_UPDATE_PENDING` under `all`), optional GSI column filters, and `cursor` for pagination.
 *
 * @param {{
 *   sendType: string,
 *   queueFilter: string | null,
 *   columnFilters?: Record<string, string>,
 *   cursor?: string | null
 * }} params
 * @returns {Promise<{ notifications: Record<string, unknown>[], nextCursor: string | null }>}
 */
const fetchNotificationsPage = async ({
  sendType,
  queueFilter,
  columnFilters = {},
  cursor,
}) => {
  const queryParams = buildNotificationsFilterQueryParams({
    sendType,
    queueFilter,
    columnFilters,
  });

  if (cursor) {
    queryParams.cursor = cursor;
  }

  return await get({
    endpoint: '/notifications',
    queryParams,
  });
};

/**
 * Downloads a CSV of all notifications matching the current queue filters (no pagination).
 * Calls `GET /notifications/csv` with the same query parameters as {@link fetchNotificationsPage} (except `cursor`).
 * Returns the raw `Response` so the caller can read a CSV blob (save filename is chosen client-side).
 *
 * @param {{
 *   sendType: string,
 *   queueFilter: string | null,
 *   columnFilters?: Record<string, string>
 * }} params
 * @returns {Promise<Response>}
 */
const fetchNotificationsCsv = async ({
  sendType,
  queueFilter,
  columnFilters = {},
}) => {
  return await get({
    endpoint: '/notifications/csv',
    queryParams: buildNotificationsFilterQueryParams({
      sendType,
      queueFilter,
      columnFilters,
    }),
    returnRawResponse: true,
  });
};

/**
 * Fetches the total count of notifications for the current filters (entire result set, not the loaded page).
 * Calls `GET /notifications/count` with the same filter query params as {@link fetchNotificationsPage} (no `cursor`).
 *
 * @param {{
 *   sendType: string,
 *   queueFilter: string | null,
 *   columnFilters?: Record<string, string>
 * }} params
 * @returns {Promise<{ count: number }>}
 */
const fetchNotificationsCount = async ({
  sendType,
  queueFilter,
  columnFilters = {},
}) => {
  return await get({
    endpoint: '/notifications/count',
    queryParams: buildNotificationsFilterQueryParams({
      sendType,
      queueFilter,
      columnFilters,
    }),
  });
};

/**
 * Fetches a single notification's detail payload for the details drawer.
 * Calls `GET /notifications/details?notificationId=…`.
 * @param {{ notificationId: string }} params
 * @returns {Promise<Record<string, unknown>>} Details payload (includes `type` for channel, member fields, preview fields, optional `errors[]`); shape is stub until DynamoDB is wired.
 */
const fetchNotificationDetails = async ({ notificationId }) => {
  return await get({
    endpoint: '/notifications/details',
    queryParams: {
      notificationId,
    },
  });
};

/**
 * POST `/notifications/bulkChangeSendType` — all rows matching filters; body matches list/csv/count query keys plus `targetSendType`.
 * @param {{ sendType: string, queueFilter: string | null, columnFilters?: Record<string, string>, targetSendType: 'manual' | 'automated' }} params
 * @returns {Promise<{ success?: boolean }>}
 */
const postNotificationsBulkChangeSendType = async ({
  sendType,
  queueFilter,
  columnFilters = {},
  targetSendType,
}) => {
  return await post({
    endpoint: '/notifications/bulkChangeSendType',
    body: {
      ...buildNotificationsFilterQueryParams({
        sendType,
        queueFilter,
        columnFilters,
      }),
      targetSendType,
    },
  });
};

/**
 * POST `/notifications/bulkSend` — send-now for all rows matching filters.
 * @param {{ sendType: string, queueFilter: string | null, columnFilters?: Record<string, string> }} params
 * @returns {Promise<{ success?: boolean }>}
 */
const postNotificationsBulkSend = async ({
  sendType,
  queueFilter,
  columnFilters = {},
}) => {
  return await post({
    endpoint: '/notifications/bulkSend',
    body: buildNotificationsFilterQueryParams({
      sendType,
      queueFilter,
      columnFilters,
    }),
  });
};

/**
 * POST `/notifications/bulkDelete` — delete all rows matching filters.
 * @param {{ sendType: string, queueFilter: string | null, columnFilters?: Record<string, string> }} params
 * @returns {Promise<{ success?: boolean }>}
 */
const postNotificationsBulkDelete = async ({
  sendType,
  queueFilter,
  columnFilters = {},
}) => {
  return await post({
    endpoint: '/notifications/bulkDelete',
    body: buildNotificationsFilterQueryParams({
      sendType,
      queueFilter,
      columnFilters,
    }),
  });
};

/**
 * POST `/notifications/send` — send one notification (list row `id` / v3 `messageId`).
 * @param {{ notificationId: string }} params
 * @returns {Promise<{ success?: boolean }>}
 */
const postNotificationSend = async ({ notificationId }) => {
  return await post({
    endpoint: '/notifications/send',
    body: { notificationId },
  });
};

/**
 * POST `/notifications/changeSendType` — move one notification between manual and automated.
 * @param {{ notificationId: string, sendType: 'manual' | 'automated' }} params
 * @returns {Promise<{ success?: boolean }>}
 */
const postNotificationChangeSendType = async ({ notificationId, sendType }) => {
  return await post({
    endpoint: '/notifications/changeSendType',
    body: { notificationId, sendType },
  });
};

/**
 * POST `/notifications/delete` — delete one notification.
 * @param {{ notificationId: string }} params
 * @returns {Promise<{ success?: boolean }>}
 */
const postNotificationDelete = async ({ notificationId }) => {
  return await post({
    endpoint: '/notifications/delete',
    body: { notificationId },
  });
};

/**
 * Sends a direct email via the notifications service.
 * Calls `POST /notifications/sendDirectEmail`.
 * @param {object} body Request body per the sendDirectEmail contract.
 * @returns {Promise<unknown>} Parsed JSON response from the backend.
 */
const sendDirectEmail = async (body) => {
  return await post({
    endpoint: '/notifications/sendDirectEmail',
    body,
  });
};

/**
 * Fetches the list of enabled email templates available for the Test Email Template form.
 * Calls `GET /notifications/sendDirectEmail/templates`.
 * @returns {Promise<{ templates: { template: string, enabled: true }[] }>}
 */
const fetchEmailTemplateList = async () => {
  return await get({
    endpoint: '/notifications/sendDirectEmail/templates',
  });
};

/**
 * Fetches the typed variable map (`name → "string" | "boolean"`) and optional subject
 * pre-fill for a specific email template.
 * Calls `GET /notifications/sendDirectEmail/templates/:template/variables`.
 * @param {string} template The template id (e.g. `"CP-23"`).
 * @returns {Promise<{ template: string, variables: Record<string, string>, subject?: string }>}
 */
const fetchEmailTemplateVariables = async (template) => {
  return await get({
    endpoint: `/notifications/sendDirectEmail/templates/${template}/variables`,
  });
};

const fetchCategories = async (benefitPlanTemplateKey) => {
  const results = await get({
    endpoint: '/template/getCategories',
    queryParams: {
      benefitPlanTemplateKey,
    },
  });

  const options = results.aggregations.unique_categories.buckets.map(
    (bucket) => ({
      value: bucket.key, //benefitPlanTemplateKey
      label: `${bucket.key} (${bucket.doc_count})`,
    }),
  );

  return options;
};

const fetchSlotNames = async (benefitPlanTemplateKey) => {
  const results = await get({
    endpoint: '/hrp-proxy/benefitPlan/db/getSlotNames',
    // queryParams: {
    //     benefitPlanTemplateKey
    // }
  });

  const options = results.map((row) => ({
    value: row.slotName,
    label: `${row.slotName}`,
  }));

  return options;
};

const fetchTemplateRiderComp = async (queryParams) => {
  // const key = `fetchTemplateRiderComp - ${JSON.stringify(queryParams)}`
  // if (myCache.has(key)) {
  //     return myCache.get(key);
  // }

  const results = await get({
    endpoint: '/template/search',
    queryParams,
  });

  // myCache.set(key, results);
  return results;
};

const fetchBnftPlanComp = async ({
  benefitPlanCompKey,
  includeProvisions = true,
  limit = 10,
  offset = 0,
}) => {
  const bnftPlanCompResult = await get({
    endpoint: '/hrp-proxy/benefitPlan/db/getBenefitPlanComponents',
    queryParams: {
      limit,
      offset,
      benefitPlanCompKey,
    },
  });

  if (includeProvisions && bnftPlanCompResult.rows) {
    const populatedRows = await Promise.all(
      bnftPlanCompResult.rows.map(async (bpcr) => {
        if (bpcr.bnftPlanCompHistFactKey !== null) {
          const benefitPlanCompProvFact = await fetchBnftPlanCompProvFact({
            bnftPlanCompHistFactKey: bpcr.bnftPlanCompHistFactKey,
          });
          return { ...bpcr, benefitPlanCompProvFact };
        }
        return bpcr;
      }),
    );

    return { ...bnftPlanCompResult, rows: populatedRows };
  }

  return bnftPlanCompResult;
};

const fetchBenefitTier = async ({ benefitTierKey, limit = 10, offset = 0 }) => {
  const key = benefitTierKey;
  if (myCache.has(key)) {
    //  console.log('Cache hit for:', key);
    return myCache.get(key);
  }

  const bnftPlanCompProvFact = await get({
    endpoint: '/hrp-proxy/benefitPlan/db/getBenefitTier',
    queryParams: {
      limit,
      offset,
      benefitTierKey,
    },
  });

  myCache.set(key, bnftPlanCompProvFact?.rows);
  return bnftPlanCompProvFact?.rows;
};

async function batchFunction({ data, func, batchSize = 10 }) {
  while (data.length) {
    console.log(`Batching data count: ${data.length}`);
    await Promise.all([...data.splice(0, batchSize).map(func)]);
  }
}

const fetchBnftPlanCompProvFact = async ({
  bnftPlanCompHistFactKey,
  limit = 10,
  offset = 0,
}) => {
  const key = `fetchBnftPlanCompProvFact - ${bnftPlanCompHistFactKey}`;
  if (myCache.has(key)) {
    //  console.log('Cache hit for:', key);
    return myCache.get(key);
  }

  const bnftPlanCompProvFact = await get({
    endpoint: '/hrp-proxy/benefitPlan/db/getBenefitPlanComponentsProvFact',
    queryParams: {
      limit,
      offset,
      bnftPlanCompHistFactKey,
    },
  });

  const populatedRows = await Promise.all(
    bnftPlanCompProvFact.rows.map(async (bpcpf) => {
      if (bpcpf.benefitTierKey !== null) {
        // const benefitTier = await fetchBenefitTier({ benefitTierKey: bpcpf.benefitTierKey })
        return {
          ...bpcpf,
          benefitTier: myCache.get(bpcpf.benefitTierKey)?.tierTypeName,
        };
      }
      return bpcpf;
    }),
  );
  myCache.set(key, populatedRows);
  return populatedRows;
};

const getCodeSet = async (codeSetName) => {
  return await get({
    endpoint: '/hrp-proxy/code-entry/v4/getAll',
    queryParams: { codeSetName },
  });
};

const getAccountTypes = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/account/db/getAccountTypes',
    queryParams,
  });
};

const getBillingCycles = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/bill/db/getBillingCycles',
    queryParams,
  });
};

const getBillingRateAndComponents = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/bill/db/getBillingRateAndComponents',
    queryParams,
  });
};

const getPostalCodeByKey = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/postalcode/db/getPostalCodeByKey',
    queryParams: {
      limit: 1,
      offset: 0,
      ...queryParams,
    },
  });
};

const getAccountsByParentAccountKeyAndAccountLevel = async (queryParams) => {
  return await get({
    endpoint:
      '/hrp-proxy/account/db/getAccountsByParentAccountKeyAndAccountLevel',
    queryParams: {
      limit: 10,
      offset: 0,
      // accountLevel: 0,
      ...queryParams,
    },
  });
};

const fetchTaxEntities = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/tax/db/getTaxEntities',
    queryParams: {
      limit: 20,
      offset: 0,
      ...queryParams,
    },
  });
};

const fetchTaxEntity = async ({ taxId, taxName }) => {
  if (!taxId && !taxName) {
    throw Error('Need taxId or taxName');
  }
  const data = await get({
    endpoint: '/hrp-proxy/tax/db/getTaxEntities',
    queryParams: {
      taxId,
      taxName,
    },
  });

  return data.rows[0];
};

/**
 * HRP getAccountsView interpolates filters into Oracle REGEXP_LIKE
 * `'^(${value})'` literals. Double apostrophes so names like
 * "Family and Children's" do not close the string (ORA-00907).
 * Keep prefix matching — do not switch to `.*value.*` (full table scan).
 */
function escapeHrpViewFilterValue(value) {
  if (value == null) {
    return value;
  }
  return String(value).replaceAll("'", "''");
}

const fetchAccounts = async (queryParams = {}) => {
  const params = {
    limit: 10,
    offset: 0,
    accountLevel: 0,
    ...queryParams,
  };
  if (params.accountName != null) {
    params.accountName = escapeHrpViewFilterValue(params.accountName);
  }
  return await get({
    endpoint: '/hrp-proxy/account/db/getAccountsView',
    queryParams: params,
  });
};

const fetchAccount = async (body) => {
  return await post({
    endpoint: '/hrp-proxy/account/v4/getAll',
    body,
  });
};

const submitAccount = async (body) => {
  return await post({
    endpoint: '/hrp-proxy/account/v4/submit',
    body,
  });
};

const submitAccountBatchJob = async (body) => {
  return await post({
    endpoint: '/hrp-proxy/account/v4/submit-batch-job',
    body,
  });
};

const submitBillingRateComponent = async (body) => {
  return await post({
    endpoint: '/hrp-proxy/bill/v4/createBillingRateComponent',
    body,
  });
};

const submitBillingRate = async (body) => {
  return await post({
    endpoint: '/hrp-proxy/bill/v4/createBillingRate',
    body,
  });
};

const submitBillingRateBatchJob = async ({
  components,
  rates,
  concurrency,
} = {}) => {
  return await post({
    endpoint: '/hrp-proxy/bill/v4/submit-billing-rate-batch-job',
    body: {
      components,
      rates,
      ...(concurrency != null ? { concurrency } : {}),
    },
  });
};

const fetchBillingRateSubCategories = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/bill/db/getBillingRateSubCategories',
    queryParams,
  });
};

const fetchBillingRateCategories = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/bill/db/getBillingRateCategories',
    queryParams,
  });
};

const checkBillingRateComponentCollisions = async (candidates) => {
  return await post({
    endpoint: '/hrp-proxy/bill/db/checkBillingRateComponentCollisions',
    body: { candidates },
  });
};

const fetchBillingRateByBenefitPlanKey = async ({ benefitPlanKey }) => {
  return await get({
    endpoint: '/hrp-proxy/bill/db/getBillingRateByBenefitPlanKey',
    queryParams: { benefitPlanKey },
  });
};

const fetchBillingRateComponentsByAccountKey = async ({
  accountKey,
  asOfDate,
  effectiveDateKey,
}) => {
  return await get({
    endpoint: '/hrp-proxy/bill/db/getBillingRateComponentsByAccountKey',
    queryParams: { accountKey, asOfDate, effectiveDateKey },
  });
};

const fetchBillingRateAndComponents = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/bill/db/getBillingRateAndComponents',
    queryParams,
  });
};

const fetchBillingRateUploadUrl = async (body) => {
  return await post({ endpoint: '/finance/billing-rate-upload-url', body });
};

const fetchHrpSubmissionTarget = async () => {
  return await get({ endpoint: '/finance/hrp-submission-target' });
};

const createBillingRateSubmission = async (body) => {
  return await post({ endpoint: '/finance/billing-rate-submission', body });
};

const fetchBillingRatePresignedUrl = async ({
  fileType,
  fileName,
  submissionId,
}) => {
  return await get({
    endpoint: '/finance/presigned-url',
    queryParams: { fileType, fileName, submissionId },
  });
};

const fetchDocumentUploadUrl = async (body) => {
  return await post({ endpoint: '/analytics/document-upload-url', body });
};

const createBillingRateSubmissionAsync = async (body) => {
  return await post({ endpoint: '/finance/billing-rate-submission', body });
};

const fetchBillingRateSubmission = async (sk) => {
  return await get({
    endpoint: `/finance/billing-rate-submission/${encodeURIComponent(sk)}`,
  });
};

const updateBillingRateSubmission = async (body) => {
  return await put({ endpoint: '/finance/billing-rate-submission', body });
};

const appendFilesToBillingRateSubmission = async ({ sk, files }) => {
  return await put({
    endpoint: '/finance/billing-rate-submission',
    body: { sk, appendFiles: files },
  });
};

const removeFilesFromBillingRateSubmission = async ({ sk, fileNames }) => {
  return await put({
    endpoint: '/finance/billing-rate-submission',
    body: { sk, removeFiles: fileNames },
  });
};

const fetchBillingRateSubmissions = async (queryParams) => {
  return await get({
    endpoint: '/finance/billing-rate-submissions',
    queryParams,
  });
};

const deleteBillingRateSubmission = async (sk) => {
  return await fetchDelete({
    endpoint: `/finance/billing-rate-submission/${encodeURIComponent(sk)}`,
    body: {},
  });
};

const submitBillingRateAudit = (body) => {
  post({ endpoint: '/audit/submit-billingRate', body }).catch((err) => {
    console.warn('[audit] billing rate audit failed:', err);
  });
};

//benefit plan
const fetchBenefitPlansByAccountKey = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/benefitPlan/db/getBenefitPlansByAccountKey',
    queryParams,
  });
};

const fetchBenefitPlans = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/benefitPlan/db/getBenefitPlansView',
    queryParams: {
      limit: 10,
      offset: 0,
      ...queryParams,
    },
  });
};

const fetchBenefitPlanDetail = async (body) => {
  return await post({
    endpoint: '/hrp-proxy/benefitPlan/v4/getBenefitPlan',
    body,
  });
};

const fetchGetLatestTemplateRiderComponent = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/benefitPlan/db/getLatestTemplateRiderComponent',
    queryParams,
  });
};

const fetchTemplatesFromHrpProxy = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/benefitPlan/db/getBenefitPlanTemplates',
    queryParams: {
      limit: 10,
      offset: 0,
      ...queryParams,
    },
  });
};

const fetchRidersFromHrp = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/benefitPlan/db/getBenefitPlanRiderHistFact',
    queryParams: {
      limit: 10,
      offset: 0,
      ...queryParams,
    },
  });
};

const fetchAssistants = async (queryParams) => {
  return await get({
    endpoint: '/qInConnect/list-assistants',
    queryParams,
  });
};

const fetchAssistantAssociation = async (queryParams) => {
  return await get({
    endpoint: '/qInConnect/list-assistant-association',
    queryParams,
  });
};

const fetchKnowledgeBase = async (queryParams) => {
  return await get({
    endpoint: '/qInConnect/get-knowledge-base',
    queryParams,
  });
};

const fetchContents = async (queryParams) => {
  return await get({
    endpoint: '/qInConnect/list-contents',
    queryParams,
  });
};

const fetchContent = async (queryParams) => {
  return await get({
    endpoint: '/qInConnect/get-content',
    queryParams,
  });
};

const fetchTagResource = async (body) => {
  return await put({
    endpoint: '/qInConnect/tag-resource',
    body,
  });
};

const fetchUnTagResource = async (body) => {
  return await put({
    endpoint: '/qInConnect/untag-resource',
    body,
  });
};

const fetchCoverageTierSetHistoryFact = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/benefitPlan/db/getCoverageTierSetHistoryFact',
    queryParams: {
      limit: 10,
      offset: 0,
      coverageTierSetStatus: 'a',
      ...queryParams,
    },
  });
};

const fetchCoverageTierSetByBenefitPlan = async ({ benefitPlanHccId }) => {
  return await get({
    endpoint: '/hrp-proxy/benefitPlan/db/getCoverageTierSetByBenefitPlan',
    queryParams: { benefitPlanHccId },
  });
};

const fetchComponentsByTemplate = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/benefitPlan/db/getComponentsByTemplate',
    queryParams: {
      limit: 10,
      offset: 0,
      ...queryParams,
    },
  });
};

const fetchValueListBytemplate = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/benefitPlan/db/getValueListBytemplate',
    queryParams: {
      limit: 10,
      offset: 0,
      ...queryParams,
    },
  });
};

const fetchValueFromValueList = async (queryParams, limit) => {
  return await get({
    endpoint: '/hrp-proxy/benefitPlan/db/getValueByVariableName',
    queryParams: {
      limit: limit || 10,
      offset: 0,
      ...queryParams,
    },
  });
};

const getProducts = async (params) => {
  return await get({
    endpoint: '/hrp-proxy/product/db/findAndCountAll',
  });
};

const getNetworks = async (params) => {
  return await get({
    endpoint: '/hrp-proxy/network/db/findAll',
  });
};

//bnftPlanHstFctKey
const getBnftPlnHstFctToNeDefComp = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/network/db/getBnftPlnHstFctToNeDefComp',
    queryParams: {
      limit: 10,
      offset: 0,
      ...queryParams,
    },
  });
};

//networkDefCompKey
const getNetworkDefCompHistFact = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/network/db/getNetworkDefCompHistFact',
    queryParams: {
      limit: 10,
      offset: 0,
      ...queryParams,
    },
  });
};

const fetchNetworkDefCompDetails = async (queryParams) =>
  get({
    endpoint: '/hrp-proxy/network/db/getNetworkDefCompDetails',
    queryParams,
  });

const submitJobBenefitPlan = async ({ bodyString }) => {
  return await post({
    endpoint: '/hrp-proxy/benefitPlan/v4/submit-job',
    bodyString,
  });
};

const submitDepartmentBatchJob = async ({ body }) => {
  return await post({
    endpoint: '/hrp-proxy/department/v4/submit-batch-job',
    body,
  });
};

const fetchServiceCategoryHistFact = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/service/db/getServiceCategoryHistFact',
    queryParams: {
      limit: 10,
      offset: 0,
      ...queryParams,
    },
  });
};

const fetchServCategoryXServCdRange = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/service/db/getServCategoryXServCdRange',
    queryParams: {
      limit: 10,
      offset: 0,
      ...queryParams,
    },
  });
};

const fetchServiceCodeRangeFact = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/service/db/getServiceCodeRangeFact',
    queryParams: {
      limit: 10,
      offset: 0,
      ...queryParams,
    },
  });
};

const fetchServicesAll = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/service/db/getServicesAll',
    queryParams: {
      limit: 10,
      offset: 0,
      ...queryParams,
    },
  });
};

const fetchServiceTypeNames = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/service/db/getServiceTypeNames',
    queryParams: {
      limit: 10,
      offset: 0,
      ...queryParams,
    },
  });
};

//searchText
const fetchServices = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/service/db/getServices',
    queryParams: {
      limit: 10,
      offset: 0,
      ...queryParams,
    },
  });
};

const fetchDiagnosis = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/diagnosis/db/getDiagnosis',
    queryParams: {
      limit: 10,
      offset: 0,
      ...queryParams,
    },
  });
};

const fetchCreateTicket = async (body) => {
  return await post({
    endpoint: '/support/create-ticket',
    body,
  });
};

const fetchAllTickets = async (queryParams) => {
  return await get({
    endpoint: '/support/all-tickets',
    queryParams,
  });
};

const fetchKanbanTickets = async (queryParams) => {
  return await get({
    endpoint: '/support/kanban-tickets',
    queryParams,
  });
};

const fetchupdateToKanbanBoard = async (body) => {
  return await put({
    endpoint: '/support/update-to-kanban-board',
    body,
  });
};

const fetchUpdateAssingTo = async (body) => {
  return await put({
    endpoint: '/support/update-assign-to',
    body,
  });
};

const fetchupdateLane = async (body) => {
  return await put({
    endpoint: '/support/update-lane',
    body,
  });
};

const fetchClaimFact = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/claim/db/getClaimFact',
    queryParams: {
      limit: 10,
      offset: 0,
      ...queryParams,
    },
  });
};

const fetchClaimsByAccountKey = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/claim/db/getClaimsByAccountKey',
    queryParams: {
      limit: 20,
      offset: 0,
      ...queryParams,
    },
  });
};

const fetchClaimDetails = async (body) => {
  return await post({
    endpoint: '/hrp-proxy/claim/v4/get',
    body,
  });
};

const fetchClaimDetailsByHccID = async (body) => {
  return await post({
    endpoint: '/hrp-proxy/claim/classic/getClaimDetailsByHccID',
    body,
  });
};

const fetchMembersView = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/member/db/getMembersViewV2',
    queryParams: {
      limit: 10,
      offset: 0,
      ...queryParams,
    },
  });
};

const fetchMemberDetailsByHccId = async (body) => {
  return await post({
    endpoint: '/hrp-proxy/member/classic/getMemberDetailsByHccId',
    body,
  });
};

const fetchAddressByAddressKey = async (postalAddressKey) => {
  return await get({
    endpoint: `/hrp-proxy/postal-address/db/getPostalAddress?postalAddressKey=${postalAddressKey}`,
  });
};

const fetchPaymentTypeCodes = async (postalAddressKey) => {
  return await get({
    endpoint: `/hrp-proxy/payeeBankAccounts/db/getPaymentTypeCodes`,
  });
};

const getDateByDateKey = async (dateKey) => {
  return await get({
    endpoint: `/hrp-proxy/dateDimension/getDateByDateKey/${dateKey}`,
  });
};

const fetchSyncRider = async (body) => {
  return await post({
    endpoint: '/sync',
    body,
  });
};

const fetchGetApiRequestJobStatus = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/job/getApiRequestJobStatus',
    queryParams,
  });
};

const fetchAllJobs = async (queryParams) => {
  return await get({
    endpoint: '/edi/all-jobs',
    queryParams,
  });
};

const fetchJobHasError = async (queryParams) => {
  return await get({
    endpoint: '/edi/job-has-error',
    queryParams,
  });
};

const fetchAllJobsByRange = async (queryParams) => {
  return await get({
    endpoint: '/edi/all-jobs-by-range',
    queryParams,
  });
};

const fetchHrpTransactionsByMemberHccId = async (queryParams) => {
  return await get({
    endpoint: '/hrp/transaction/getTransactionsByMemberHccId',
    queryParams,
  });
};

const fetchJvTransactionsByMemberUuid = async (queryParams) => {
  return await get({
    endpoint: '/jv/transaction/getTransactionsByPatientMemberUuid',
    queryParams,
  });
};

//fileName
const fetchAllLines = async (queryParams) => {
  return await get({
    endpoint: '/edi/all-lines',
    queryParams,
  });
};

const fetchRerunJob = async (body) => {
  return await post({
    endpoint: '/edi/rerun-job',
    body,
  });
};

const fetchAllUsers = async (queryParams) => {
  return await get({
    endpoint: '/access/all-users',
    queryParams,
  });
};
const fetchRoleMatrix = async () => {
  return await get({
    endpoint: '/access/role-matrix',
  });
};
const fetchAuthToken = async (queryParams) => {
  return await get({
    endpoint: '/access/get-token',
    queryParams,
  });
};
const updateUserRoles = async (body) => {
  return await put({
    endpoint: '/access/update-roles',
    body,
  });
};

const createUser = async (body) => {
  return await post({
    endpoint: '/access/create-user',
    body,
  });
};

const fetchCurrentUser = async () => {
  return await get({
    endpoint: '/access/me',
  });
};

const updateUserSettings = async (body) => {
  return await put({
    endpoint: '/access/settings',
    body,
  });
};

const fetchAvatarUrl = async (email) => {
  return await get({
    endpoint: '/access/avatar-url',
    queryParams: { email },
  });
};

const fetchAvatarPresign = async () => {
  return await get({
    endpoint: '/access/avatar-presign',
  });
};

const uploadUserAvatar = async (body) => {
  return await post({
    endpoint: '/access/avatar',
    body,
  });
};

const deleteUserAvatar = async () => {
  return await fetchDelete({
    endpoint: '/access/avatar',
  });
};

const fetchSubmitEnrollmentSpares = async (body) => {
  return await post({
    endpoint: '/hrp-proxy/enrollment/v4/submitEnrollmentSparse',
    body,
  });
};

const fetchBenefitProvisionFact = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/benefitPlan/db/getBenefitProvisionFact',
    queryParams: {
      limit: 10,
      offset: 0,
      ...queryParams,
    },
  });
};

const fetchBenefitProvision = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/benefitPlan/db/getBenefitProvision',
    queryParams: {
      limit: 10,
      offset: 0,
      ...queryParams,
    },
  });
};

const fetchProvisionDetailsByBenefitPlan = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/benefitPlan/db/getProvisionDetailsByBenefitPlan',
    queryParams: {
      limit: 10,
      offset: 0,
      ...queryParams,
    },
  });
};

const fetchBenefitPlanComponents = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/benefitPlan/db/getBenefitPlanComponents',
    queryParams: {
      limit: 10,
      offset: 0,
      ...queryParams,
    },
  });
};

const submitBenefitPlanAudit = async ({ body }) => {
  return await post({
    endpoint: '/audit/submit-benefitPlan',
    body,
  });
};

const fetchLogsByBenefitPlanHccId = async (queryParams) => {
  return await get({
    endpoint: '/audit/logs-by-benefitplan-hccid',
    queryParams,
  });
};

const submitV2Audit = async ({
  entityType,
  entityId,
  action,
  email,
  request,
  response,
}) => {
  if (!entityType || !entityId || !action || !email || !request || !response) {
    throw new Error('You need all keys for this one.');
  }
  return await post({
    endpoint: '/audit/v2',
    body: { entityType, entityId, action, email, request, response },
  });
};

const getV2Audit = async ({
  email,
  entityType,
  entityId,
  lastEvaluatedKey,
}) => {
  return await get({
    endpoint: '/audit/v2',
    queryParams: { email, entityType, entityId, lastEvaluatedKey },
  });
};

const fetchSuppliers = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/suppliers/db/getSuppliers',
    queryParams: {
      limit: 10,
      offset: 0,
      ...queryParams,
    },
  });
};

const fetchSupplierContracts = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/suppliers/db/getSupplierContracts',
    queryParams,
  });
};

const fetchFeeComponents = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/suppliers/db/getFeeComponents',
    queryParams,
  });
};

const fetchFeeComponentDetails = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/suppliers/db/getFeeComponentDetails',
    queryParams,
  });
};

const fetchHrpProviderNetworkList = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/provider/db/networks',
    queryParams,
  });
};

const fetchSuppliersBySupplierNetwork = async ({
  supplierNetworkHccId,
  ...queryParams
}) => {
  return await get({
    endpoint: `/hrp-proxy/provider/db/supplier-networks/${encodeURIComponent(supplierNetworkHccId)}/suppliers`,
    queryParams,
  });
};

const fetchPractitionersBySupplierNetwork = async ({
  supplierNetworkHccId,
  ...queryParams
}) => {
  return await get({
    endpoint: `/hrp-proxy/provider/db/supplier-networks/${encodeURIComponent(supplierNetworkHccId)}/practitioners`,
    queryParams,
  });
};

const fetchBenefitNetworksBySupplierNetwork = async ({
  supplierNetworkHccId,
  ...queryParams
}) => {
  return await get({
    endpoint: `/hrp-proxy/provider/db/supplier-networks/${encodeURIComponent(supplierNetworkHccId)}/benefit-networks`,
    queryParams,
  });
};

const fetchSuppliersByBenefitNetwork = async ({
  benefitNetworkId,
  ...queryParams
}) => {
  return await get({
    endpoint: `/hrp-proxy/provider/db/benefit-networks/${encodeURIComponent(benefitNetworkId)}/suppliers`,
    queryParams,
  });
};

const fetchPractitionersByBenefitNetwork = async ({
  benefitNetworkId,
  ...queryParams
}) => {
  return await get({
    endpoint: `/hrp-proxy/provider/db/benefit-networks/${encodeURIComponent(benefitNetworkId)}/practitioners`,
    queryParams,
  });
};

const fetchPractitionersBySupplier = async ({
  supplierHccId,
  ...queryParams
}) => {
  return await get({
    endpoint: `/hrp-proxy/provider/db/suppliers/${encodeURIComponent(supplierHccId)}/practitioners`,
    queryParams,
  });
};

const fetchSupplierNetworksByBenefitNetwork = async ({
  benefitNetworkId,
  ...queryParams
}) => {
  return await get({
    endpoint: `/hrp-proxy/provider/db/benefit-networks/${encodeURIComponent(benefitNetworkId)}/supplier-networks`,
    queryParams,
  });
};

const fetchHrpPractitionerRoleList = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/provider/db/practitioners',
    queryParams,
  });
};

const fetchPractitioner = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/practitioner/db/getPractitioners',
    queryParams: {
      limit: 10,
      offset: 0,
      ...queryParams,
    },
  });
};

const fetchPractitionerRole = async (body) => {
  return await post({
    endpoint: '/hrp-proxy/practitioner/v4/getPractitionerRole',
    body,
  });
};

const fetchProviderNetworks = async (body) => {
  return await post({
    endpoint: '/hrp-proxy/benefitPlan/classic/getProviderNetworks',
    body,
  });
};

const fetchMemberBySystemIdentifier = async (queryParams) => {
  return await get({
    endpoint: '/member/getMemberBySystemIdentifier',
    queryParams,
  });
};

const fetchAccumulatorDetails = async (body) => {
  return await post({
    endpoint: '/hrp-proxy/accumulator/classic/getAccumulatorDetails',
    body,
  });
};

const fetchAccAdjustHistFact = async (body) => {
  return await post({
    endpoint: '/hrp-proxy/accumulator/db/getAccAdjustHistFact',
    body: {
      offset: 0,
      limit: 20,
      ...body,
    },
  });
};

const fetchAuthorizations = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/authorization/db/getAuthorizationsView',
    queryParams,
  });
};

const fetchAuthorizationById = async (body) => {
  return await post({
    endpoint: '/hrp-proxy/authorization/v5/getAuthorization',
    body,
  });
};

const fetchHrpCodeEntries = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/code-entry/db/getCodeEntries',
    queryParams,
  });
};

const fetchPlaceOfService = async (queryParams) =>
  get({ endpoint: '/hrp-proxy/code-entry/db/getPlaceOfService', queryParams });

const fetchTypeOfBill = async (queryParams) =>
  get({ endpoint: '/hrp-proxy/code-entry/db/getTypeOfBill', queryParams });

const fetchModifiers = async (queryParams) =>
  get({ endpoint: '/hrp-proxy/code-entry/db/getModifiers', queryParams });

const fetchProcedureCodes = async (queryParams) =>
  get({ endpoint: '/hrp-proxy/code-entry/db/getProcedureCodes', queryParams });

const fetchProviderTaxonomy = async (queryParams) =>
  get({
    endpoint: '/hrp-proxy/code-entry/db/getProviderTaxonomy',
    queryParams,
  });

const fetchCodeSetNames = async (queryParams) =>
  get({ endpoint: '/hrp-proxy/code-entry/db/getCodeSetNames', queryParams });

const fetchConditionCodes = async (queryParams) =>
  get({ endpoint: '/hrp-proxy/code-entry/db/getConditionCodes', queryParams });

const fetchOccurrenceCodes = async (queryParams) =>
  get({ endpoint: '/hrp-proxy/code-entry/db/getOccurrenceCodes', queryParams });

const fetchMemberByMemberUuid = async (queryParams) => {
  return await get({
    endpoint: '/member/getMembersByMemberUuid',
    queryParams,
  });
};

//member care chat
const fetchMemberCareChatList = async (queryParams) => {
  return await get({
    endpoint: '/member-care-chat/list',
    queryParams,
  });
};

const postMemberCareChat = async (body) => {
  return await post({
    endpoint: '/member-care-chat',
    body,
    returnRawResponse: true,
  });
};

const fetchGetState = async (queryParams) => {
  return await get({
    endpoint: '/check-pointer/getState',
    queryParams,
  });
};

const deleteCheckpoint = async (body) => {
  return await fetchDelete({
    endpoint: '/check-pointer',
    body,
  });
};

const fetchOpensearchGetAggs = async (body) => {
  return await post({
    endpoint: '/opensearch/getAggs',
    body,
  });
};

const fetchGetDocuments = async (body) => {
  return await post({
    endpoint: '/opensearch/getDocuments',
    body,
    // queryParams: {
    //     index: 'knowledge-base'
    // }
  });
};

const fetchPreSignedUrl = async (body) => {
  return await post({
    endpoint: '/s3/getPresignedUrl',
    body,
  });
};

//TODO refactor
const fetchBenefits = async (body) => {
  const url = `${HPR_BASE_URL}/benefitPlan/classic/getBenefits`;
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  const result = await res.json();
  return result;
};

const fetchDescribeLogGroups = async (queryParams) => {
  return await get({
    endpoint: '/cloudwatch/describeLogGroups',
    queryParams,
  });
};

const fetchDescribeLogStreams = async (queryParams) => {
  return await get({
    endpoint: '/cloudwatch/describeLogStreams',
    queryParams,
  });
};

const fetchStartLiveTail = async (body) => {
  return await post({
    endpoint: '/cloudwatch/startLiveTail',
    body,
  });
};

const fetchLogEvents = async (body) => {
  return await post({
    endpoint: '/cloudwatch/getLogEvents',
    body,
  });
};

const fetchJvClients = async (queryParams) => {
  const endpoint = `/jv-proxy/db/getClients`;
  return await get({ endpoint, queryParams });
};

const fetchJvBrokers = async (queryParams) => {
  const endpoint = `/jv-proxy/db/getBrokers`;
  return await get({ endpoint, queryParams });
  // let endpoint = `/jv-proxy/pureProxy/billing/searchbroker/all`;
  // if (queryParams?.nextPageUrl) {
  //     endpoint = `/jv-proxy/pureProxy/${queryParams.nextPageUrl}`
  // }
  // return await get({ endpoint });
};

const fetchJvBankAccountByClient = async (queryParams) => {
  const endpoint = `/athena/jv/getBankAccountByClient`;
  return await get({ endpoint, queryParams });
};

const fetchJvPaymentsByClientId = async (queryParams) => {
  const endpoint = `/athena/jv/getPaymentsByClientId`;
  return await get({ endpoint, queryParams });
};

const fetchDailyJournal = async (queryParams) => {
  const endpoint = `/jv-proxy/db/getDailyCheckJournal`;
  return await get({ endpoint, queryParams });
};

const fetchReadJvBroker = async ({ code }) => {
  const endpoint = `/jv-proxy/pureProxy/billing/readbroker/Code/${code}`;
  return await get({ endpoint });
};

const fetchReadJvClient = async ({ clientId, blkofbusid }) => {
  const endpoint = `/jv-proxy/pureProxy/core/readclients/clientID/${clientId}/blkofbusid/${blkofbusid}`;
  return await get({ endpoint });
};

const asJvArray = (value) => {
  if (value == null || value === '') {
    return [];
  }
  if (Array.isArray(value)) {
    return value.filter((item) => item != null);
  }
  if (typeof value === 'object') {
    return [value];
  }
  return [];
};

const collectStopLossContractNames = (readClientResult) => {
  const names = new Set();
  for (const client of asJvArray(readClientResult?.Response?.Client)) {
    if (client?.StopLossContract) {
      names.add(String(client.StopLossContract).trim());
    }
    for (const dept of asJvArray(client?.ClientDepartment)) {
      if (dept?.StopLossContract) {
        names.add(String(dept.StopLossContract).trim());
      }
    }
  }
  return [...names].filter(Boolean);
};

const fetchSearchJvSpecStopLossContract = async ({
  StopLossContractName,
  EffectiveDate,
} = {}) => {
  const endpoint = queryParamsToPathParams({
    path: `/jv-proxy/pureProxy/reinsurance/searchspeccontract`,
    params: { StopLossContractName, EffectiveDate },
  });
  return await get({ endpoint });
};

const toJvIsoDate = (value) => {
  if (value == null || String(value).trim() === '') {
    return null;
  }
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    return s.slice(0, 10);
  }
  const mdy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mdy) {
    return `${mdy[3]}-${String(mdy[1]).padStart(2, '0')}-${String(mdy[2]).padStart(2, '0')}`;
  }
  return s;
};

const flattenReinsuranceTracker = (payload) => {
  const response = payload?.Response || payload || {};
  const summary =
    response.SummaryAndAmounts &&
    typeof response.SummaryAndAmounts === 'object' &&
    !Array.isArray(response.SummaryAndAmounts)
      ? response.SummaryAndAmounts
      : {};
  const { MetaData, Pagination, SummaryAndAmounts, Content, ...rest } =
    response;
  return { ...rest, ...summary };
};

const isJvNoRecordsError = (err) =>
  /ITEMNF|No records found matching search criteria/i.test(
    String(err?.message || ''),
  );

const formatJvErrorMessage = (err) => {
  const raw = String(err?.message || '').trim();
  if (!raw) {
    return 'Something went wrong';
  }
  try {
    const parsed = JSON.parse(raw);
    const messages = parsed?.Response?.Messages || {};
    const xml = String(messages.ValidationErrors || '');
    const fields = [...xml.matchAll(/<([^/>]+)>([^<]*)<\/\1>/g)]
      .map((match) => String(match[2] || '').trim())
      .filter(Boolean);
    if (fields.length) {
      return fields.join(' · ');
    }
    if (messages.Information) {
      return String(messages.Information);
    }
  } catch {
    // not a JV JSON error body
  }
  return raw;
};

const fetchJvSlAccumulator = async (queryParams) => {
  const endpoint = queryParamsToPathParams({
    path: `/jv-proxy/pureProxy/reinsurance/slaccumulator`,
    params: queryParams,
  });
  try {
    return await get({ endpoint });
  } catch (err) {
    if (isJvNoRecordsError(err)) {
      return { Response: {} };
    }
    throw new Error(formatJvErrorMessage(err));
  }
};

const fetchJvSpecStopLossContractsByNames = async (inputNames) => {
  const names = [
    ...new Set((inputNames || []).map((n) => String(n).trim()).filter(Boolean)),
  ];
  const errors = [];

  const groups = await Promise.all(
    names.map(async (name) => {
      let summaries = [];
      try {
        const search = await fetchSearchJvSpecStopLossContract({
          StopLossContractName: name,
        });
        summaries = asJvArray(search?.Response?.SpecStopLossContract);
      } catch (err) {
        errors.push({
          name,
          message: err?.message
            ? String(err.message).slice(0, 180)
            : 'Search failed',
        });
      }

      if (!summaries.length) {
        summaries = [{ StopLossContractName: name }];
      }

      return Promise.all(
        summaries.map(async (summary) => {
          const contractName = summary?.StopLossContractName || name;
          const effectiveDate = toJvIsoDate(summary?.EffectiveDate);
          let tracker = null;
          let trackerError = null;
          if (contractName && effectiveDate) {
            try {
              const payload = await fetchJvSlAccumulator({
                StopLossContractName: contractName,
                EffectiveDate: effectiveDate,
              });
              tracker = flattenReinsuranceTracker(payload);
            } catch (err) {
              trackerError = err?.message
                ? String(err.message).slice(0, 180)
                : 'Tracker failed';
              errors.push({ name: contractName, message: trackerError });
            }
          } else {
            trackerError =
              'Need contract name and effective date to load the reinsurance tracker.';
          }

          return {
            name: contractName,
            summary,
            tracker,
            trackerError,
            response: { SpecStopLossContract: summary },
          };
        }),
      );
    }),
  );

  const contracts = groups.flat().sort((a, b) => {
    const aDate = a?.summary?.EffectiveDate || '';
    const bDate = b?.summary?.EffectiveDate || '';
    return String(bDate).localeCompare(String(aDate));
  });

  return { contracts, names, errors };
};

const fetchJvSpecStopLossContractsForClient = async (readClientResult) => {
  return fetchJvSpecStopLossContractsByNames(
    collectStopLossContractNames(readClientResult),
  );
};

const rewriteJvRestLink = (link) => {
  if (!link || typeof link !== 'string') {
    return null;
  }
  const raw = link.trim();
  const restIndex = raw.toLowerCase().indexOf('/rest/');
  const restPath =
    restIndex >= 0
      ? raw.slice(restIndex + '/rest/'.length)
      : raw.replace(/^https?:\/\/[^/]+\//i, '').replace(/^\//, '');
  if (!restPath) {
    return null;
  }
  return `/jv-proxy/pureProxy/${restPath}`;
};

const fetchJvByRestLink = async (link) => {
  const endpoint = rewriteJvRestLink(link);
  if (!endpoint) {
    throw new Error('Missing Javelina link');
  }
  return await get({ endpoint });
};

const fetchJvEmployees = async (queryParams) => {
  let endpoint = queryParamsToPathParams({
    path: `/jv-proxy/pureProxy/eligibility/searchMembers`,
    params: queryParams,
  });

  if (queryParams?.nextPageUrl) {
    endpoint = `/jv-proxy/pureProxy/${queryParams.nextPageUrl}`;
  }

  return await get({ endpoint });
};

const fetchJvClaims = async (queryParams) => {
  //     let endpoint = `/jv-proxy/pureProxy/claim/searchClaims/patientId/${patientId}`;
  // if (dependentId) {
  //     endpoint += `/DepSequenceNumber/${dependentId}`
  // }

  let endpoint = queryParamsToPathParams({
    path: `/jv-proxy/pureProxy/claim/searchClaims`,
    params: queryParams,
  });

  if (queryParams?.nextPageUrl) {
    endpoint = `/jv-proxy/pureProxy/${queryParams.nextPageUrl}`;
  }

  return await get({ endpoint });
};

const fetchMemberByEmployeeId = async ({ employeeid, DepSequenceNumber }) => {
  let endpoint = `/jv-proxy/pureProxy/eligibility/members/employeeid/${employeeid}`;
  if (DepSequenceNumber) {
    endpoint += `/DepSequenceNumber/${DepSequenceNumber}`;
  }

  return await get({ endpoint });
};

const fetchHrpTableItemsByHccID = async (queryParams) => {
  return await get({
    endpoint: '/hrp-table/getItemssByHccID',
    queryParams,
  });
};

const fetchHrpTableItemsBySparse = async (queryParams) => {
  return await get({
    endpoint: '/hrp-table/getItemsBySparse',
    queryParams,
  });
};

const fetchHrpTableItemsByPk = async (queryParams) => {
  return await get({
    endpoint: '/hrp-table/getItemsByPk',
    queryParams,
  });
};

const fetchPayorDbStatus = async () =>
  get({ endpoint: '/hrp-table/payor-db-status' });

const fetchHrpDocs = async () => get({ endpoint: '/hrp-docs' });

const fetchHrpDoc = async (key) =>
  get({ endpoint: '/hrp-docs/file', queryParams: { key } });

const fetchHrpDocBinary = async (key) =>
  get({
    endpoint: '/hrp-docs/raw',
    queryParams: { key },
    returnRawResponse: true,
  });

const fetchFinanceDocs = async () => get({ endpoint: '/finance/docs' });

const fetchFinanceDoc = async (key) =>
  get({ endpoint: '/finance/docs/file', queryParams: { key } });

const fetchFinanceDocBinary = async (key) =>
  get({
    endpoint: '/finance/docs/raw',
    queryParams: { key },
    returnRawResponse: true,
  });

const fetchHrpTableItemsBySk = async (queryParams) => {
  return await get({
    endpoint: '/hrp-table/getItemsBySk',
    queryParams,
  });
};

const fetchNotificationsTableItemsByConversationId = async (queryParams) => {
  return await get({
    endpoint: '/notification-table/getItemsByConversationId',
    queryParams,
  });
};

const fetchPcpTransactionTableScan = async (queryParams) => {
  return await get({
    endpoint: '/pcp-transaction-table/scan',
    queryParams,
  });
};

const fetchPcpTranscationsByMemberUuid = async (queryParams) => {
  return await get({
    endpoint: '/pcp-transaction-table/getPcpTranscationsByMemberUuid',
    queryParams,
  });
};

const fetchBenefitPlanTableScan = async (queryParams) => {
  return await get({
    endpoint: '/benefit-plans-table/scan',
    queryParams,
  });
};

const fetchBenefitPlanTableItemsByPk = async (queryParams) => {
  return await get({
    endpoint: '/benefit-plans-table/getItemsByPk',
    queryParams,
  });
};

const fetchNotificationsJobRegistryTableItemsByPk = async (queryParams) => {
  return await get({
    endpoint: '/notifications-job-registry-table/getItemsByPk',
    queryParams,
  });
};

const fetchNotificationsJobRegistryTableScan = async (queryParams) => {
  return await get({
    endpoint: '/notifications-job-registry-table/scan',
    queryParams,
  });
};

const fetchReferralTableItemsByPk = async (queryParams) => {
  return await get({
    endpoint: '/referral-table/getItemsByPk',
    queryParams,
  });
};

const fetchReferralTableScan = async (queryParams) => {
  return await get({
    endpoint: '/referral-table/scan',
    queryParams,
  });
};

const fetchClaimAndAccumulatorTableItemsByPk = async (queryParams) => {
  return await get({
    endpoint: '/claim-and-accumulator-table/getItemsByPk',
    queryParams,
  });
};

const fetchClaimAndAccumulatorTableScan = async (queryParams) => {
  return await get({
    endpoint: '/claim-and-accumulator-table/scan',
    queryParams,
  });
};

const fetchMemberTableItemsByPk = async (queryParams) => {
  return await get({
    endpoint: '/member/getItemsByPk',
    queryParams,
  });
};

const fetchMemberTableScan = async (queryParams) => {
  return await get({
    endpoint: '/member/scan',
    queryParams,
  });
};

const fetchDataManagementServiceMember = async (queryParams) => {
  return await get({
    endpoint: '/athena/data-management/service-member',
    queryParams,
  });
};

const fetchDataManagementServiceMemberTimestamps = async (queryParams) => {
  return await get({
    endpoint: '/athena/data-management/service-member/snapshot-timestamps',
    queryParams,
  });
};

const fetchCognitoUserPools = async () => {
  return await get({ endpoint: '/cognito/user-pools' });
};

const fetchCognitoGroups = async (userPoolId) => {
  return await get({
    endpoint: `/cognito/${encodeURIComponent(userPoolId)}/groups`,
  });
};

const fetchCognitoUsersInGroup = async (userPoolId, groupName, nextToken) => {
  return await get({
    endpoint: `/cognito/${encodeURIComponent(userPoolId)}/groups/${encodeURIComponent(groupName)}/users`,
    queryParams: nextToken ? { nextToken } : {},
  });
};

const fetchCognitoAttributes = async (userPoolId) => {
  return await get({
    endpoint: `/cognito/${encodeURIComponent(userPoolId)}/attributes`,
  });
};

const fetchCognitoUserSearch = async (userPoolId, attribute, value) => {
  return await get({
    endpoint: `/cognito/${encodeURIComponent(userPoolId)}/users/search`,
    queryParams: { attribute, value },
  });
};

const fetchHrpTableScan = async (queryParams) => {
  return await get({
    endpoint: '/hrp-table/scan',
    queryParams,
  });
};

const fetchNotificationsTableScan = async (queryParams) => {
  return await get({
    endpoint: '/notification-table/scan',
    queryParams,
  });
};

const fetchNotificationsTableItemsByPk = async (queryParams) => {
  return await get({
    endpoint: '/notification-table/getItemsByPk',
    queryParams,
  });
};

const fetchNotificationScheduleTableItemsByPk = async (queryParams) => {
  return await get({
    endpoint: '/notification-schedule-table/getItemsByPk',
    queryParams,
  });
};

const fetchItemsByRetryOn = async (queryParams) => {
  return await get({
    endpoint: '/hrp-table/getItemsByRetryOn',
    queryParams,
  });
};

const fetchBusinessTableScan = async (queryParams) => {
  return await get({
    endpoint: '/business-table/scan',
    queryParams,
  });
};

const fetchBusinessTableItemsByPk = async (queryParams) => {
  return await get({
    endpoint: '/business-table/getItemsByPk',
    queryParams,
  });
};

const fetchBusinessTableItemsBySk = async (queryParams) => {
  return await get({
    endpoint: '/business-table/getItemsBySk',
    queryParams,
  });
};

// Finance audit ledger (FinanceInboundInvoices) — one row per PBM invoice
// payable the PBM Invoice agent created in HRP. `queryParams` can carry any of
// { pbm, supplierHccId, submittedByUserId, from, to, lastEvaluatedKey }.
const fetchFinanceInvoices = async (queryParams) => {
  return await get({
    endpoint: '/finance-invoices/query',
    queryParams,
  });
};

const fetchFinanceInvoicesByPk = async (queryParams) => {
  return await get({
    endpoint: '/finance-invoices/getItemsByPk',
    queryParams,
  });
};

const financeOpsGet = (path, queryParams) =>
  get({ endpoint: `/finance-ops${path}`, queryParams });

const financeOpsPost = (path, body) =>
  post({ endpoint: `/finance-ops${path}`, body });

const fetchBillRuns = async (queryParams) =>
  get({ endpoint: '/hrp-proxy/bill/db/listBillRuns', queryParams });

const fetchAccountBillingCycle = async (queryParams) =>
  get({
    endpoint: '/hrp-proxy/account/db/getAccountBillingCycle',
    queryParams,
  });

const fetchBillRunDetail = async (queryParams) =>
  get({ endpoint: '/hrp-proxy/bill/db/getBillRunDetail', queryParams });

const fetchBillRetroLines = async (queryParams) =>
  get({ endpoint: '/hrp-proxy/bill/db/getBillRetroLines', queryParams });

const fetchFundingRequests = async (queryParams) =>
  get({ endpoint: '/hrp-proxy/bill/db/listFundingRequests', queryParams });

const fetchFundingRequestDetail = async (queryParams) =>
  get({ endpoint: '/hrp-proxy/bill/db/getFundingRequestDetail', queryParams });

const fetchAccountFundingOverview = async (queryParams) =>
  get({
    endpoint: '/hrp-proxy/bill/db/getAccountFundingOverview',
    queryParams,
  });

const fetchFundingRequestClaims = async (queryParams) =>
  get({ endpoint: '/hrp-proxy/bill/db/getFundingRequestClaims', queryParams });

const fetchPremiumPayments = async (queryParams) =>
  get({ endpoint: '/hrp-proxy/bill/db/listPremiumPayments', queryParams });

const fetchPremiumPaymentDetail = async (queryParams) =>
  get({ endpoint: '/hrp-proxy/bill/db/getPremiumPaymentDetail', queryParams });

const fetchAccountBillingRates = async (queryParams) =>
  get({ endpoint: '/hrp-proxy/bill/db/getAccountBillingRates', queryParams });

const fetchAccountStopLoss = async (queryParams) =>
  get({ endpoint: '/hrp-proxy/reinsurer/db/getAccountStopLoss', queryParams });

const fetchAccountStopLossProgress = async (queryParams) =>
  get({
    endpoint: '/hrp-proxy/reinsurer/db/getAccountStopLossProgress',
    queryParams,
  });

const fetchStopLossStatus = async () => get({ endpoint: '/stoploss/status' });

const triggerStopLossReport = async () =>
  post({ endpoint: '/stoploss/trigger', body: {} });

const fetchEdiExtractStatus = async () =>
  get({ endpoint: '/edi-extract/status' });

const fetchEdiExtractHistory = async () =>
  get({ endpoint: '/edi-extract/history' });

const triggerEdiExtract = async (jobs) =>
  post({ endpoint: '/edi-extract/trigger', body: jobs ? { jobs } : {} });

const fetchPlanCoverage = async (queryParams) =>
  get({ endpoint: '/hrp-proxy/benefitPlan/db/getPlanCoverage', queryParams });

const fetchPlanRiders = async (queryParams) =>
  get({ endpoint: '/hrp-proxy/benefitPlan/db/getPlanRiders', queryParams });

const fetchPlanRiderDetail = async (queryParams) =>
  get({
    endpoint: '/hrp-proxy/benefitPlan/db/getPlanRiderDetail',
    queryParams,
  });

const fetchPlanAccumulatorSetup = async (queryParams) =>
  get({
    endpoint: '/hrp-proxy/benefitPlan/db/getPlanAccumulatorSetup',
    queryParams,
  });

const fetchServiceCategoryCodes = async (queryParams) =>
  get({
    endpoint: '/hrp-proxy/benefitPlan/db/getServiceCategoryCodes',
    queryParams,
  });

const fetchAccountPlanMap = async (queryParams) =>
  get({ endpoint: '/hrp-proxy/benefitPlan/db/getAccountPlanMap', queryParams });

/** Saved Benefit Predictor question names, for the Ask tab typeahead. */
const fetchBenefitQuestions = async (queryParams) =>
  get({
    endpoint: '/hrp-proxy/benefitPlan/v5/getActiveQuestions',
    queryParams,
  });

/**
 * "Is this covered and how much", answered by HRP's own rules engine rather
 * than by parsing provision text. Omit memberHccId for a plan-only answer;
 * supply it to get live deductible / out-of-pocket balances.
 */
const fetchCoverageAnswer = async (body) =>
  post({ endpoint: '/hrp-proxy/benefitPlan/v5/getCoverageAnswer', body });

const fetchPaymentCycles = async (queryParams) =>
  get({ endpoint: '/hrp-proxy/paymentCycle/db/', queryParams });

const triggerBillRun = async (body) =>
  post({ endpoint: '/hrp-proxy/paymentCycle/classic/triggerBillRun', body });

const triggerPaymentRun = async (body) =>
  post({ endpoint: '/hrp-proxy/paymentCycle/classic/triggerPaymentRun', body });

const submitHrpFundingPayment = async (body) =>
  post({ endpoint: '/hrp-proxy/funding/v4/submitFundingPayment', body });

const submitHrpPremiumPayment = async (body) =>
  post({ endpoint: '/hrp-proxy/premiumPayment/v4/submitPremiumPayment', body });

const submitHrpPayable = async (body) =>
  post({ endpoint: '/hrp-proxy/payable/v4/submitPayable', body });

const submitHrpReceivable = async (body) =>
  post({ endpoint: '/hrp-proxy/receivable/v4/submitReceivable', body });

const fetchAccountBankAccounts = async (queryParams) =>
  get({
    endpoint: '/hrp-proxy/payeeBankAccounts/db/getAccountBankAccounts',
    queryParams,
  });

const fetchFinanceOpsJobs = async (queryParams) =>
  financeOpsGet('/jobs', queryParams);

const postFinanceOpsJob = async (body) => financeOpsPost('/jobs', body);

const patchFinanceOpsJob = async (id, body) =>
  put({ endpoint: `/finance-ops/jobs/${encodeURIComponent(id)}`, body });

const postFinanceOpsReadiness = async (body) =>
  financeOpsPost('/readiness/evaluate', body);

const postFinanceOpsApplyPayment = async (body) =>
  financeOpsPost('/payments/apply', body);

const postFinanceOpsApplyAdjustment = async (body) =>
  financeOpsPost('/adjustments/apply', body);

const postFinanceOpsInvoicePack = async (body) =>
  financeOpsPost('/invoice-packs', body);

const fetchReprocessCdc = async (body) => {
  return await post({
    endpoint: '/hrp-cdc/reprocess',
    body,
  });
};

const fetchAllBankAccounts = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/payeeBankAccounts/db/getAllBankAccounts',
    queryParams: {
      limit: 10,
      offset: 0,
      ...queryParams,
    },
  });
};

const fetchBankAccountDetails = async (queryParams) => {
  return await get({
    endpoint: '/business-table/getItemsByPk',
    queryParams: {
      limit: 10,
      offset: 0,
      ...queryParams,
    },
  });
};

const fetchPayeeBankAccountsFromDB = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/payeeBankAccounts/db/getPayeeBankAccounts',
    queryParams: {
      limit: 10,
      offset: 0,
      ...queryParams,
    },
  });
};

const fetchPayeeBankAccounts = async (body) => {
  return await post({
    endpoint: '/hrp-proxy/payeeBankAccounts/v5/getPayeeBankAccounts',
    body,
  });
};

const fetchAllPayment = async (queryParams) => {
  return await get({
    endpoint: '/hrp-proxy/payment/db/getPayments',
    queryParams: {
      limit: 10,
      offset: 0,
      ...queryParams,
    },
  });
};

const fetchJobs = async (queryParams) => {
  return await get({
    endpoint: '/job-registry/job',
    queryParams,
  });
};

const fetchJobRegistryJobHasError = async (queryParams) => {
  return await get({
    endpoint: '/job-registry/job/has-error',
    queryParams,
  });
};

const fetchJobItemsByJobId = async (queryParams) => {
  return await get({
    endpoint: '/job-registry/job/items',
    queryParams,
  });
};

const fetchTags = async (queryParams) => {
  return await get({
    endpoint: '/job-registry/job/query-tags',
    queryParams,
  });
};
const fetchGroups = async (queryParams) => {
  return await get({
    endpoint: '/job-registry/group',
    queryParams,
  });
};

const fetchCreateGroups = async (body) => {
  return await post({
    endpoint: '/job-registry/group/create',
    body,
  });
};

const fetchUsersInGroup = async (queryParams) => {
  return await get({
    endpoint: '/job-registry/group/users',
    queryParams,
  });
};

const fetchRemoveUsersFromGroup = async (body) => {
  return await fetchDelete({
    endpoint: '/job-registry/group/remove-users',
    body,
  });
};

const fetchAddUsersToGroup = async (body) => {
  return await post({
    endpoint: '/job-registry/group/add-users',
    body,
  });
};

const fetchTagsInGroup = async (queryParams) => {
  return await get({
    endpoint: '/job-registry/group/tags',
    queryParams,
  });
};

const fetchAddTagsToGroup = async (body) => {
  return await post({
    endpoint: '/job-registry/group/add-tags',
    body,
  });
};

const fetchRemoveTagsFromGroup = async (body) => {
  return await fetchDelete({
    endpoint: '/job-registry/group/remove-tags',
    body,
  });
};

const fetchBlogPosts = async ({ cursor, limit = 8, author } = {}) => {
  return await get({
    endpoint: '/blog/posts',
    queryParams: { cursor, limit, author },
  });
};

const fetchBlogAuthors = async () => {
  return await get({
    endpoint: '/blog/authors',
  });
};

const fetchBlogPost = async (id) => {
  return await get({
    endpoint: `/blog/posts/${id}`,
  });
};

const searchBlogPosts = async (q, { from, to } = {}) => {
  return await get({
    endpoint: '/blog/search',
    queryParams: { q, from, to },
  });
};

const createBlogPost = async (body) => {
  return await post({
    endpoint: '/blog/posts',
    body,
  });
};

const updateBlogPost = async (id, body) => {
  return await put({
    endpoint: `/blog/posts/${id}`,
    body,
  });
};

const deleteBlogPost = async (id) => {
  return await fetchDelete({
    endpoint: `/blog/posts/${id}`,
  });
};

const enhanceBlogBody = async ({ title, body }) => {
  return await post({
    endpoint: '/blog/enhance',
    body: { title, body },
  });
};

const talkBlogWriter = async ({ messages, title, body }) => {
  return await post({
    endpoint: '/blog/writer',
    body: { messages, title, body },
  });
};

const fetchBlogComments = async (id, { cursor, limit = 6 } = {}) => {
  return await get({
    endpoint: `/blog/posts/${id}/comments`,
    queryParams: { cursor, limit },
  });
};

const createBlogComment = async (id, body) => {
  return await post({
    endpoint: `/blog/posts/${id}/comments`,
    body,
  });
};

const rateBlogPost = async (id, stars) => {
  return await put({
    endpoint: `/blog/posts/${id}/rating`,
    body: { stars },
  });
};

const submitBlogReview = async (id, { stars, body }) => {
  return await post({
    endpoint: `/blog/posts/${id}/review`,
    body: { stars, body },
  });
};

const fetchBlogPresign = async ({ fileName, contentType, postId }) => {
  return await get({
    endpoint: '/blog/presign',
    queryParams: { fileName, contentType, postId },
  });
};

const fetchBlogImageUrl = async (key) => {
  return await get({
    endpoint: '/blog/image-url',
    queryParams: { key },
  });
};

const serializePlanManagerLastEvaluatedKey = (lastEvaluatedKey) => {
  if (lastEvaluatedKey === undefined || lastEvaluatedKey === null) {
    return undefined;
  }

  if (typeof lastEvaluatedKey === 'string') {
    return lastEvaluatedKey;
  }

  return JSON.stringify(lastEvaluatedKey);
};

/**
 * Fetches one page of Javelina client config for Plan Manager plans grid.
 * @param {object | string | undefined} [lastEvaluatedKey]
 * @returns {Promise<object | null>}
 */
const fetchPlanManagerClientConfig = async (lastEvaluatedKey) => {
  const serializedLastEvaluatedKey =
    serializePlanManagerLastEvaluatedKey(lastEvaluatedKey);

  return await get({
    endpoint: '/plan-manager/clients/config',
    queryParams: serializedLastEvaluatedKey
      ? { lastEvaluatedKey: serializedLastEvaluatedKey }
      : {},
  });
};

/**
 * Fetches one page of HealthEdge client config for Plan Manager plans grid.
 * @param {object | string | undefined} [lastEvaluatedKey]
 * @returns {Promise<object | null>}
 */
const fetchPlanManagerHealthEdgeConfig = async (lastEvaluatedKey) => {
  const serializedLastEvaluatedKey =
    serializePlanManagerLastEvaluatedKey(lastEvaluatedKey);

  return await get({
    endpoint: '/plan-manager/healthedge/config',
    queryParams: serializedLastEvaluatedKey
      ? { lastEvaluatedKey: serializedLastEvaluatedKey }
      : {},
  });
};

/**
 * Fetches Javelina plan rows in batch for Plan Manager plans grid.
 * @param {Array<{ clientId: string, planId: string }>} planRefs
 * @returns {Promise<object | null>}
 */
const fetchPlanManagerPlansBatch = async (planRefs) => {
  return await post({
    endpoint: '/plan-manager/plans/batch',
    body: planRefs,
  });
};

/**
 * Fetches HealthEdge plan rows in batch for Plan Manager plans grid.
 * @param {Array<{ clientId: string, planId: string }>} planRefs
 * @returns {Promise<object | null>}
 */
const fetchPlanManagerHealthEdgePlansBatch = async (planRefs) => {
  return await post({
    endpoint: '/plan-manager/healthedge/plans/batch',
    body: planRefs,
  });
};

/**
 * Fetches the default notification catalog for Plan Manager.
 * @returns {Promise<object | null>}
 */
const fetchPlanManagerDefaultNotificationConfig = async () => {
  return await get({
    endpoint: '/plan-manager/plans/default-notification-config',
  });
};

/**
 * Persists a plan's notification configuration.
 * @param {Object} body - `{ clientId, planId, notificationConfig }`.
 * @returns {Promise<object | null>}
 */
const fetchPlanManagerUpdateNotificationConfig = async (body) => {
  return await put({
    endpoint: '/plan-manager/plans/notification-config',
    body,
  });
};

const searchClientDocuments = async (body) => {
  return await post({
    url: CLIENT_DOCUMENT_URL,
    endpoint: '/clientDocuments/search',
    body,
  });
};

/**
 * Persists a form-shaped medical plan via the v2 BFF path.
 * The caller omits plan-row uuid and networkAffiliation.
 * @param {Object} body - Form-shaped medical plan v2 update body.
 * @returns {Promise<object | null>} Parsed body on success (including empty `{}`), otherwise null.
 */
const fetchPlanManagerUpdateMedicalPlanV2 = async (body) => {
  return await put({
    endpoint: '/plan-manager/plans/v2/updateMedicalPlan',
    body,
  });
};

const updateDocumentStatus = async (body) => {
  return await put({
    url: CLIENT_DOCUMENT_URL,
    endpoint: '/clientDocuments/updateStatus',
    body,
  });
};

/**
 * Requests a signed S3 PUT URL for a plan SBC document.
 * @param {{ fileName: string, fileType: string }} params
 * @returns {Promise<{ signedUrl: string, publicObjectUrl: string }>}
 */
const fetchPlanManagerSbcUploadUrl = async ({ fileName, fileType }) => {
  return await get({
    endpoint: '/plan-manager/files/upload/sbc',
    queryParams: { fileName, fileType },
  });
};

/**
 * Requests a signed S3 PUT URL for a plan SPD document.
 * @param {{ fileName: string, fileType: string }} params
 * @returns {Promise<{ signedUrl: string, publicObjectUrl: string }>}
 */
const fetchPlanManagerSpdUploadUrl = async ({ fileName, fileType }) => {
  return await get({
    endpoint: '/plan-manager/files/upload/spd',
    queryParams: { fileName, fileType },
  });
};

/**
 * Uploads a file directly to a pre-signed S3 PUT URL. Credentials are omitted
 * and the raw response is returned because S3 rejects cookies and replies with
 * an empty body.
 * @param {string} signedUrl
 * @param {Blob} file
 * @param {string} contentType - Must match the type the URL was signed for.
 * @returns {Promise<Response>}
 */
const uploadPlanManagerFileToSignedUrl = async (
  signedUrl,
  file,
  contentType,
) => {
  return await put({
    url: signedUrl,
    endpoint: '',
    rawBody: file,
    contentType,
    credentials: 'omit',
    returnRawResponse: true,
  });
};

/**
 * Fetches Plan Manager settings for Plan Setup dropdowns.
 * @returns {Promise<object | null>}
 */
const fetchPlanManagerSettings = async () => {
  return await get({
    endpoint: '/plan-manager/settings',
  });
};

/**
 * Fetches Plan Manager OE client configs for Networks and Pre-Enrollment lists.
 * @param {{ shouldGetHrpPlans?: boolean, isCustom?: boolean }} [options]
 * @returns {Promise<Array<object> | null>}
 */
const fetchPlanManagerClientConfigs = async ({
  shouldGetHrpPlans = false,
  isCustom = false,
} = {}) => {
  return await get({
    endpoint: '/plan-manager/oe-config/client-configs',
    queryParams: {
      shouldGetHrpPlans: shouldGetHrpPlans ? 'true' : undefined,
      isCustom: isCustom ? 'true' : undefined,
    },
  });
};

/**
 * Fetches one OE client config by client name for Networks Config hydrate.
 * @param {string} clientName
 * @returns {Promise<object | null>}
 */
const fetchPlanManagerClientConfigByName = async (clientName) => {
  return await get({
    endpoint: `/plan-manager/oe-config/client-configs/${encodeURIComponent(clientName)}`,
  });
};

/**
 * Persists pre-enrollment custom (site customization) content for an OE client.
 * @param {string} clientName
 * @param {Object} body - Flat custom-content wire map.
 * @returns {Promise<object | null>}
 */
const fetchPlanManagerUpdatePreEnrollmentCustom = async (clientName, body) => {
  return await put({
    endpoint: `/plan-manager/oe-config/client-configs/${encodeURIComponent(clientName)}/pre-enrollment-custom`,
    body,
  });
};

const createCheckRegisterUploadUrl = async (body) => {
  return await post({
    url: CLIENT_DOCUMENT_URL,
    endpoint: '/clientDocuments/createCheckRegisterUploadUrl',
    body,
  });
};

/**
 * Creates an OE client config via the v2 BFF path (caller omits network-identity UUIDs).
 * @param {Object} body - v2 OE create body.
 * @returns {Promise<object | null>}
 */
const fetchPlanManagerCreateClientConfigV2 = async (body) => {
  return await post({
    endpoint: '/plan-manager/oe-config/v2/client-configs',
    body,
  });
};

/**
 * Updates an OE client config via the v2 BFF path (caller omits network-identity UUIDs).
 * @param {string} clientName
 * @param {Object} body - v2 OE update body.
 * @returns {Promise<object | null>}
 */
const fetchPlanManagerUpdateClientConfigV2 = async (clientName, body) => {
  return await put({
    endpoint: `/plan-manager/oe-config/v2/client-configs/${encodeURIComponent(clientName)}`,
    body,
  });
};

const rejectCheckRegister = async (body) => {
  return await put({
    url: CLIENT_DOCUMENT_URL,
    endpoint: '/clientDocuments/rejectCheckRegister',
    body,
  });
};

/**
 * Publishes OE config for a client (sets preEnrollment.publish true on the server).
 * @param {string} clientName
 * @returns {Promise<object | null>}
 */
const fetchPlanManagerPublishOeConfig = async (clientName) => {
  return await post({
    endpoint: `/plan-manager/oe-config/publish/${encodeURIComponent(clientName)}`,
  });
};

/**
 * Unpublishes OE config for a client (sets preEnrollment.publish false on the server).
 * @param {string} clientName
 * @returns {Promise<object | null>}
 */
const fetchPlanManagerUnpublishOeConfig = async (clientName) => {
  return await post({
    endpoint: `/plan-manager/oe-config/unpublish/${encodeURIComponent(clientName)}`,
  });
};

/**
 * Fetches Centivo network-series dropdown options for Networks Config.
 * @returns {Promise<Array<object> | null>}
 */
const fetchPlanManagerNetworkSeriesOptions = async () => {
  return await get({
    endpoint: '/plan-manager/oe-config/network-series',
  });
};

/**
 * Validates a pre-enrollment OE URL for uniqueness and format.
 * @param {string} url
 * @returns {Promise<object | null>}
 */
const fetchPlanManagerValidateOeUrl = async (url) => {
  return await get({
    endpoint: '/plan-manager/oe-config/validate-url',
    queryParams: { url },
  });
};

/**
 * Fetches a Javelina medical plan export payload.
 * @param {string} clientId
 * @param {string} planId
 * @returns {Promise<object | null>}
 */
const fetchPlanManagerMedicalPlan = async (clientId, planId) => {
  return await get({
    endpoint: '/plan-manager/plans/medical',
    queryParams: { clientId, planId },
  });
};

/**
 * Fetches a HealthEdge medical plan export payload.
 * @param {string} clientId
 * @param {string} planId
 * @returns {Promise<object | null>}
 */
const fetchPlanManagerHealthEdgeMedicalPlan = async (clientId, planId) => {
  return await get({
    endpoint: '/plan-manager/healthedge/plans/medical',
    queryParams: { clientId, planId },
  });
};

/**
 * Starts a Javelina (JV) plan-manager list refresh job.
 * @returns {Promise<{ jobId: string } | null>}
 */
const startPlanManagerJavelinaRefresh = async () => {
  return await post({
    endpoint: '/plan-manager/plans/refresh',
    body: {},
  });
};

/**
 * Starts a HealthEdge (HRP) plan-manager list refresh job.
 * @returns {Promise<{ jobId: string } | null>}
 */
const startPlanManagerHealthEdgeRefresh = async () => {
  return await post({
    endpoint: '/plan-manager/healthedge/refresh',
    body: {},
  });
};

/**
 * Polls plan-manager refresh job status.
 * @param {string} jobId
 * @returns {Promise<{
 *   jobId: string,
 *   status: string,
 *   percent: number,
 *   stage: string,
 *   message: string,
 * } | null>}
 */
const fetchPlanManagerRefreshJobStatus = async (jobId) => {
  return await get({
    endpoint: `/plan-manager/refresh/jobs/${encodeURIComponent(jobId)}`,
  });
};
const fetchAdjustmentPayableReasonCodes = async (queryParams = {}) => {
  return await get({
    endpoint: '/hrp-proxy/payable/db/getAdjustmentPayableReasonCodes',
    queryParams,
  });
};

const bulkUpdateDocumentStatus = async (body) => {
  return await post({
    url: CLIENT_DOCUMENT_URL,
    endpoint: '/clientDocuments/bulkUpdateStatus',
    body,
  });
};

const bulkDocumentsZip = async (items) =>
    post({
        url: CLIENT_DOCUMENT_URL,
        endpoint: "/clientDocuments/bulkDocumentsZip",
        body: { items },
    });

const fetchClientDocumentSignedUrl = async (key) => {
  return await get({
    url: CLIENT_DOCUMENT_URL,
    endpoint: '/clientDocuments/signedUrl',
    queryParams: { key },
  });
};

export {
  appendFilesToBillingRateSubmission,
  bulkUpdateDocumentStatus,
  checkBillingRateComponentCollisions,
  createBillingRateSubmission,
  createBillingRateSubmissionAsync,
  createBlogComment,
  createBlogPost,
  createCheckRegisterUploadUrl,
  createUser,
  deleteBillingRateSubmission,
  deleteBlogPost,
  deleteCheckpoint,
  deleteUserAvatar,
  enhanceBlogBody,
  escapeHrpViewFilterValue,
  fetchAccAdjustHistFact,
  fetchAccount,
  fetchAccountBankAccounts,
  fetchAccountBillingCycle,
  fetchAccountBillingRates,
  fetchAccountFundingOverview,
  fetchAccountPlanMap,
  fetchAccountStopLoss,
  fetchAccountStopLossProgress,
  fetchAccounts,
  fetchAccumulatorDetails,
  fetchAddTagsToGroup,
  fetchAddUsersToGroup,
  fetchAddressByAddressKey,
  fetchAdjustmentPayableReasonCodes,
  fetchAllBankAccounts,
  fetchAllJobs,
  fetchAllJobsByRange,
  fetchAllLines,
  fetchAllPayment,
  fetchAllTickets,
  fetchAllUsers,
  fetchAssistantAssociation,
  fetchAssistants,
  fetchAuthToken,
  fetchAuthorizationById,
  fetchAuthorizations,
  fetchAvatarPresign,
  fetchAvatarUrl,
  fetchBankAccountDetails,
  fetchBenefitNetworksBySupplierNetwork,
  fetchBenefitPlanComponents,
  fetchBenefitPlanDetail,
  fetchBenefitPlanTableItemsByPk,
  fetchBenefitPlanTableScan,
  fetchBenefitPlans,
  fetchBenefitPlansByAccountKey,
  fetchBenefitProvision,
  fetchBenefitProvisionFact,
  fetchBenefitQuestions,
  fetchBenefitTier,
  fetchBenefits,
  fetchBillRetroLines,
  fetchBillRunDetail,
  fetchBillRuns,
  fetchBillingRateAndComponents,
  fetchBillingRateByBenefitPlanKey,
  fetchBillingRateCategories,
  fetchBillingRateComponentsByAccountKey,
  fetchBillingRatePresignedUrl,
  fetchBillingRateSubCategories,
  fetchBillingRateSubmission,
  fetchBillingRateSubmissions,
  fetchBillingRateUploadUrl,
  fetchBlogAuthors,
  fetchBlogComments,
  fetchBlogImageUrl,
  fetchBlogPost,
  fetchBlogPosts,
  fetchBlogPresign,
  fetchBnftPlanComp,
  fetchBnftPlanCompProvFact,
  fetchBusinessTableItemsByPk,
  fetchBusinessTableItemsBySk,
  fetchBusinessTableScan,
  fetchCategories,
  fetchClaimAndAccumulatorTableItemsByPk,
  fetchClaimAndAccumulatorTableScan,
  fetchClaimDetails,
  fetchClaimDetailsByHccID,
  fetchClaimFact,
  fetchClaimsByAccountKey,
  fetchClientDocumentSignedUrl,
  fetchCodeSetNames,
  fetchCognitoAttributes,
  fetchCognitoGroups,
  fetchCognitoUserPools,
  fetchCognitoUserSearch,
  fetchCognitoUsersInGroup,
  fetchComponentsByTemplate,
  fetchConditionCodes,
  fetchContent,
  fetchContents,
  fetchCoverageAnswer,
  fetchCoverageTierSetByBenefitPlan,
  fetchCoverageTierSetHistoryFact,
  fetchCreateGroups,
  fetchCreateTicket,
  fetchCurrentUser,
  fetchDailyJournal,
  fetchDataManagementServiceMember,
  fetchDataManagementServiceMemberTimestamps,
  fetchDescribeLogGroups,
  fetchDescribeLogStreams,
  fetchDiagnosis,
  fetchDocumentUploadUrl,
  fetchEdiExtractHistory,
  fetchEdiExtractStatus,
  fetchEmailTemplateList,
  fetchEmailTemplateVariables,
  fetchFeeComponentDetails,
  fetchFeeComponents,
  fetchFinanceDoc,
  fetchFinanceDocBinary,
  fetchFinanceDocs,
  fetchFinanceInvoices,
  fetchFinanceInvoicesByPk,
  fetchFinanceOpsJobs,
  fetchFundingRequestClaims,
  fetchFundingRequestDetail,
  fetchFundingRequests,
  fetchGetApiRequestJobStatus,
  fetchGetDocuments,
  fetchGetLatestTemplateRiderComponent,
  fetchGetState,
  fetchGroups,
  fetchHrpCodeEntries,
  fetchHrpDoc,
  fetchHrpDocBinary,
  fetchHrpDocs,
  fetchHrpPractitionerRoleList,
  fetchHrpProviderNetworkList,
  fetchHrpSubmissionTarget,
  fetchHrpTableItemsByHccID,
  fetchHrpTableItemsByPk,
  fetchHrpTableItemsBySk,
  fetchHrpTableItemsBySparse,
  fetchHrpTableScan,
  fetchHrpTransactionsByMemberHccId,
  fetchItemsByRetryOn,
  fetchJobHasError,
  fetchJobItemsByJobId,
  fetchJobRegistryJobHasError,
  fetchJobs,
  fetchJvBankAccountByClient,
  fetchJvBrokers,
  fetchJvByRestLink,
  fetchJvClaims,
  fetchJvClients,
  fetchJvEmployees,
  fetchJvPaymentsByClientId,
  fetchJvSlAccumulator,
  fetchJvSpecStopLossContractsByNames,
  fetchJvSpecStopLossContractsForClient,
  fetchJvTransactionsByMemberUuid,
  fetchKanbanTickets,
  fetchKnowledgeBase,
  fetchLogEvents,
  fetchLogsByBenefitPlanHccId,
  fetchMemberByEmployeeId,
  fetchMemberByMemberUuid,
  fetchMemberBySystemIdentifier,
  fetchMemberCareChatList,
  fetchMemberDetailsByHccId,
  fetchMemberTableItemsByPk,
  fetchMemberTableScan,
  fetchMembersView,
  fetchModifiers,
  fetchNetworkDefCompDetails,
  fetchNotificationDetails,
  fetchNotificationScheduleTableItemsByPk,
  fetchNotificationTemplateList,
  fetchNotificationsCount,
  fetchNotificationsCsv,
  fetchNotificationsJobRegistryTableItemsByPk,
  fetchNotificationsJobRegistryTableScan,
  fetchNotificationsPage,
  fetchNotificationsTableItemsByConversationId,
  fetchNotificationsTableItemsByPk,
  fetchNotificationsTableScan,
  fetchOccurrenceCodes,
  fetchOpensearchGetAggs,
  fetchPayeeBankAccounts,
  fetchPayeeBankAccountsFromDB,
  fetchPaymentCycles,
  fetchPaymentTypeCodes,
  fetchPayorDbStatus,
  fetchPcpTransactionTableScan,
  fetchPcpTranscationsByMemberUuid,
  fetchPlaceOfService,
  fetchPlanAccumulatorSetup,
  fetchPlanCoverage,
  fetchPlanManagerClientConfig,
  fetchPlanManagerClientConfigByName,
  fetchPlanManagerClientConfigs,
  fetchPlanManagerCreateClientConfigV2,
  fetchPlanManagerDefaultNotificationConfig,
  fetchPlanManagerHealthEdgeConfig,
  fetchPlanManagerHealthEdgeMedicalPlan,
  fetchPlanManagerHealthEdgePlansBatch,
  fetchPlanManagerMedicalPlan,
  fetchPlanManagerNetworkSeriesOptions,
  fetchPlanManagerPlansBatch,
  fetchPlanManagerPublishOeConfig,
  fetchPlanManagerRefreshJobStatus,
  fetchPlanManagerSbcUploadUrl,
  fetchPlanManagerSettings,
  fetchPlanManagerSpdUploadUrl,
  fetchPlanManagerUnpublishOeConfig,
  fetchPlanManagerUpdateClientConfigV2,
  fetchPlanManagerUpdateMedicalPlanV2,
  fetchPlanManagerUpdateNotificationConfig,
  fetchPlanManagerUpdatePreEnrollmentCustom,
  fetchPlanManagerValidateOeUrl,
  fetchPlanRiderDetail,
  fetchPlanRiders,
  fetchPractitioner,
  fetchPractitionerRole,
  fetchPractitionersByBenefitNetwork,
  fetchPractitionersBySupplier,
  fetchPractitionersBySupplierNetwork,
  fetchPreSignedUrl,
  fetchPremiumPaymentDetail,
  fetchPremiumPayments,
  fetchProcedureCodes,
  fetchProviderNetworks,
  fetchProviderTaxonomy,
  fetchProvisionDetailsByBenefitPlan,
  fetchReadJvBroker,
  fetchReadJvClient,
  fetchReferralTableItemsByPk,
  fetchReferralTableScan,
  fetchRemoveTagsFromGroup,
  fetchRemoveUsersFromGroup,
  fetchReprocessCdc,
  fetchRerunJob,
  fetchRidersFromHrp,
  fetchRoleMatrix,
  fetchSearchJvSpecStopLossContract,
  fetchServCategoryXServCdRange,
  fetchServiceCategoryCodes,
  fetchServiceCategoryHistFact,
  fetchServiceCodeRangeFact,
  fetchServiceTypeNames,
  fetchServices,
  fetchServicesAll,
  fetchSlotNames,
  fetchStartLiveTail,
  fetchStopLossStatus,
  fetchSubmitEnrollmentSpares,
  fetchSupplierContracts,
  fetchSupplierNetworksByBenefitNetwork,
  fetchSuppliers,
  fetchSuppliersByBenefitNetwork,
  fetchSuppliersBySupplierNetwork,
  fetchSyncRider,
  fetchTagResource,
  fetchTags,
  fetchTagsInGroup,
  fetchTaxEntities,
  fetchTaxEntity,
  fetchTemplateRiderComp,
  fetchTemplates,
  fetchTemplatesFromHrpProxy,
  fetchTypeOfBill,
  fetchUnTagResource,
  fetchUpdateAssingTo,
  fetchUsersInGroup,
  fetchValueFromValueList,
  fetchValueListBytemplate,
  fetchupdateLane,
  fetchupdateToKanbanBoard,
  flattenReinsuranceTracker,
  getAccountTypes,
  getAccountsByParentAccountKeyAndAccountLevel,
  getBillingCycles,
  getBillingRateAndComponents,
  getBnftPlnHstFctToNeDefComp,
  getCodeSet,
  getDateByDateKey,
  getNetworkDefCompHistFact,
  getNetworks,
  getPostalCodeByKey,
  getProducts,
  getV2Audit,
  patchFinanceOpsJob,
  postFinanceOpsApplyAdjustment,
  postFinanceOpsApplyPayment,
  postFinanceOpsInvoicePack,
  postFinanceOpsJob,
  postFinanceOpsReadiness,
  postMemberCareChat,
  postNotificationChangeSendType,
  postNotificationDelete,
  postNotificationSend,
  postNotificationsBulkChangeSendType,
  postNotificationsBulkDelete,
  postNotificationsBulkSend,
  rateBlogPost,
  rejectCheckRegister,
  removeFilesFromBillingRateSubmission,
  searchBlogPosts,
  searchClientDocuments,
  sendDirectEmail,
  sendToken,
  startPlanManagerHealthEdgeRefresh,
  startPlanManagerJavelinaRefresh,
  submitAccount,
  submitAccountBatchJob,
  submitBenefitPlanAudit,
  submitBillingRate,
  submitBillingRateAudit,
  submitBillingRateBatchJob,
  submitBillingRateComponent,
  submitBlogReview,
  submitDepartmentBatchJob,
  submitHrpFundingPayment,
  submitHrpPayable,
  submitHrpPremiumPayment,
  submitHrpReceivable,
  submitJobBenefitPlan,
  submitV2Audit,
  talkBlogWriter,
  triggerBillRun,
  triggerEdiExtract,
  triggerPaymentRun,
  triggerStopLossReport,
  updateBillingRateSubmission,
  updateBlogPost,
  updateDocumentStatus,
  updateNotificationTemplateSendType,
  updateUserRoles,
  updateUserSettings,
  uploadPlanManagerFileToSignedUrl,
  uploadUserAvatar,
  verifyToken,
  bulkDocumentsZip
};
